/* eslint-disable max-classes-per-file */
/**
 * ServerSide & ClientSide 공통으로 사용할 사용자 정의 Error 정의
 *
 * @author parkyj
 * @since 2023.02.24 - 최초 작성
 */

import {
  addAsIsPcWebDomain,
  currentUrl,
  addAsIsMWebDomain,
} from './domainUtil';

export interface ApiError extends Error {
  // name: string;
  status: number;
  alertType?: 'alert' | 'confirm';
  data: any;
  message: string;
  headers?: string;
  times?: string; // 점검시 시간값
  errorCode?: number;
  redirectUrl?: string;

  notFound?: boolean;
  isHistoryBack?: boolean;
}

/**
 * ApiErrorClass
 * 용도 > 사용자 정의 Error
 */
export class ApiErrorClass extends Error implements ApiError {
  name = 'ApiErrorClass';

  status: number;

  data: any;

  message: string;

  headers?: string;

  errorCode?: number;

  times?: string; // 점검시 시간값

  redirectUrl?: string;

  notFound?: boolean;

  constructor(message: string, status: number, data: Partial<ApiError> = {}) {
    super(message);
    this.message = message;
    this.status = status;

    // data 객체의 속성들을 현재 인스턴스에 복사
    Object.assign(this, data);

    // 프로토타입 체인 설정
    Object.setPrototypeOf(this, ApiErrorClass.prototype);
  }
}

/**
 * API 에러 객체의 인스턴스 확인용 핸들러
 * @param object
 * @returns
 */
export function isInstanceOfAPIError(object: unknown): object is ApiError {
  return object instanceof ApiErrorClass;
}

export class NotFoundError extends ApiErrorClass {
  name = 'NotFoundError';

  notFound = true;

  redirectUrl = '/404';

  constructor(message = '찾을 수 없습니다.') {
    super(message, 404, { notFound: true, redirectUrl: '/404' });
  }
}

export class AuthError extends ApiErrorClass {
  name = 'AuthError';

  constructor(device: 'MO' | 'PC' | 'Android' | 'iOS', returnUrl?: string) {
    super('인증되지 않은 사용자입니다.', 401);
    if (returnUrl) {
      const setRedirectUrl = async () => {
        this.redirectUrl =
          device === 'PC'
            ? `${await addAsIsPcWebDomain(
                '/Member/Login'
              )}?rUrl=${encodeURIComponent(currentUrl())}`
            : `${await addAsIsMWebDomain(
                '/Member/Login'
              )}?rUrl=${encodeURIComponent(currentUrl())}`;
      };
      setRedirectUrl();
    }
  }
}

export class InternalServerError extends ApiErrorClass {
  name = 'InternalServerError';

  constructor(message = '알 수 없는 서버 에러') {
    super(message, 500);
  }
}

/**
 * 마이하트 API 에러 객체
 */
export class MyHeartCollectionApiError extends ApiErrorClass {
  name = 'MyHeartCollectionApiError';

  alertType = 'alert';

  constructor(message: string, code: number, returnUrl?: string) {
    super(message, code);
  }
}

/**
 * 마이하트 API 에러 객체
 */
export class MyHeartProductApiError extends ApiErrorClass {
  name = 'MyHeartProductApiError';

  alertType = 'confirm';

  constructor(message: string, code: number, returnUrl?: string) {
    super(message, code);
    this.redirectUrl = returnUrl || '/rn/myheart';
  }
}

/**
 * 마이하트 API 에러 객체
 */
export class MyHeartBrandApiError extends ApiErrorClass {
  name = 'MyHeartBrandApiError';

  alertType = 'confirm';

  constructor(message: string, code: number, returnUrl?: string) {
    super(message, code);
    const setRedirectUrl = async () => {
      this.redirectUrl = returnUrl || '/rn/myheart?tab=BRAND';
    };
    setRedirectUrl();
  }
}

/**
 * 마이하트 API 에러 객체
 */
export class MyHeartContentsApiError extends ApiErrorClass {
  name = 'MyHeartContentsApiError';

  alertType = 'confirm';

  constructor(message: string, code: number, returnUrl?: string) {
    super(message, code);
    const setRedirectUrl = async () => {
      this.redirectUrl = returnUrl || '/rn/myheart?tab=CONTENTS';
    };
    setRedirectUrl();
  }
}

/**
 * 숏폼 API 에러 객체
 */
export class ShortformApiError extends ApiErrorClass {
  name = 'ShortformApiError';

  alertType = 'alert';

  isHistoryBack = true;

  constructor(message = '페이지를 찾을 수 없습니다.') {
    super(message, 20015);
  }
}

/**
 * 스타일클립 에러 객체
 */
export class StyleclipNoNContent extends ApiErrorClass {
  name = 'StyleclipApiError';

  alertType = 'alert';

  redirectUrl = '/rn/styleclip';

  constructor(message = '유효하지 않은 콘텐츠입니다.') {
    super(message, 21103);
  }
}

export class ApiFailedError extends ApiErrorClass {
  name = 'ApiFailedError';

  constructor(message = 'API 요청에 실패하였습니다.', code = 512) {
    super(message, code);
  }
}

export class NetworkOfflineError extends ApiErrorClass {
  name = 'NetworkOfflineError';

  constructor(message = '네트워크 연결이 끊어졌습니다.') {
    super(message, 513);
  }
}
